import { useTheme } from '@mui/material/styles';
import { MarketMonth, fmt } from './api';

interface Step {
  label: string;
  kind: 'rev' | 'sub' | 'cost' | 'est' | 'ads' | 'fixed';
  value?: number;
  actual?: number;
  expected?: number;
}

/** Revenue → contribution → net, with expected (unbilled) partner costs hatched. */
export const Waterfall = ({ total, fixed }: { total: MarketMonth; fixed: number }) => {
  const theme = useTheme();
  const c = {
    line: theme.palette.divider,
    muted: theme.palette.text.secondary,
    fg: theme.palette.text.primary,
    rev: theme.palette.primary.main,
    cost: theme.palette.grey[theme.palette.mode === 'dark' ? 600 : 400],
    ads: theme.palette.secondary.main,
    fixed: theme.palette.grey[theme.palette.mode === 'dark' ? 500 : 600],
    good: theme.palette.success.main,
    bad: theme.palette.error.main,
    hatch: theme.palette.warning.main,
    hatchBg: theme.palette.mode === 'dark' ? '#3a2d17' : '#fbf0dc',
    paper: theme.palette.background.paper,
  };
  const net = total.contribution - fixed;
  const steps: Step[] = [
    { label: 'Revenue', kind: 'rev', value: total.net },
    { label: 'Crematorium', kind: 'cost', actual: total.crematorium.billed, expected: total.crematorium.expected },
    { label: 'Transport', kind: 'cost', actual: total.transport.billed, expected: total.transport.expected },
    { label: 'Drop-off', kind: 'cost', actual: total.dropoff.billed, expected: total.dropoff.expected },
    { label: 'Gov. fees', kind: 'cost', actual: total.gov },
    { label: 'Card fees', kind: 'est', actual: total.card_fee },
    { label: 'Urns, delivery', kind: 'est', actual: total.delivery },
    { label: 'Google Ads', kind: 'ads', actual: total.ads },
    { label: 'Contribution', kind: 'sub', value: total.contribution },
    { label: 'Fixed costs', kind: 'fixed', actual: fixed },
    { label: 'Net', kind: 'sub', value: net },
  ];
  const W = 900;
  const H = 300;
  const pl = 56;
  const pr = 10;
  const pt = 26;
  const pb = 40;
  const tick = total.net > 150000 ? 50000 : total.net > 40000 ? 20000 : 5000;
  const max = Math.max(tick, Math.ceil(total.net / tick) * tick);
  const min = Math.min(0, Math.floor(Math.min(net, total.contribution) / tick) * tick);
  const y = (v: number) => pt + ((max - v) / (max - min)) * (H - pt - pb);
  const bw = (W - pl - pr) / steps.length;
  const ticks: number[] = [];
  for (let v = min; v <= max; v += tick) ticks.push(v);

  let run = 0;
  const bars = steps.map((st, i) => {
    const x = pl + i * bw + bw * 0.16;
    const w = bw * 0.68;
    const label = (
      <text key="l" x={x + w / 2} y={H - pb + 18} textAnchor="middle" fontSize={11} fill={c.fg}>
        {st.label}
      </text>
    );
    if (st.kind === 'rev' || st.kind === 'sub') {
      const v = st.value || 0;
      run = v;
      const top = Math.max(v, 0);
      const bot = Math.min(v, 0);
      const col = st.kind === 'rev' ? c.rev : v >= 0 ? c.good : c.bad;
      return (
        <g key={st.label}>
          <rect x={x} y={y(top)} width={w} height={Math.max(1, y(bot) - y(top))} fill={col} rx={2} />
          <text x={x + w / 2} y={y(top) - 7} textAnchor="middle" fontSize={11.5} fontWeight={500} fill={c.fg}>
            {fmt(v)}
          </text>
          {label}
        </g>
      );
    }
    const a = st.actual || 0;
    const e = st.expected || 0;
    const start = run;
    run -= a + e;
    const col = st.kind === 'ads' ? c.ads : st.kind === 'fixed' ? c.fixed : c.cost;
    return (
      <g key={st.label}>
        {a > 0 &&
          (st.kind === 'est' ? (
            <rect
              x={x}
              y={y(start)}
              width={w}
              height={Math.max(1, y(start - a) - y(start))}
              fill={c.paper}
              stroke={c.muted}
              strokeDasharray="3 2"
            />
          ) : (
            <rect x={x} y={y(start)} width={w} height={Math.max(1, y(start - a) - y(start))} fill={col} rx={1} />
          ))}
        {e > 0 && (
          <rect
            x={x}
            y={y(start - a)}
            width={w}
            height={Math.max(1, y(start - a - e) - y(start - a))}
            fill="url(#prof-hatch)"
            stroke={c.hatch}
          />
        )}
        <text x={x + w / 2} y={y(start) - 7} textAnchor="middle" fontSize={11} fill={c.muted}>
          −{fmt(a + e)}
        </text>
        <line x1={x + w} x2={x + bw} y1={y(run)} y2={y(run)} stroke={c.muted} strokeDasharray="2 2" />
        {label}
      </g>
    );
  });

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Waterfall from revenue to net"
      style={{ display: 'block', minWidth: 680, width: '100%', height: 'auto' }}
    >
      <defs>
        <pattern id="prof-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill={c.hatchBg} />
          <rect width="2.5" height="6" fill={c.hatch} />
        </pattern>
      </defs>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={pl} x2={W - pr} y1={y(v)} y2={y(v)} stroke={c.line} />
          <text x={pl - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill={c.muted}>
            {v ? `${v / 1000}k` : '0'}
          </text>
        </g>
      ))}
      {bars}
    </svg>
  );
};
